import styled, { useTheme } from 'styled-components';
import {
  KoobInputSelect,
  KoobTooltip,
  PromotionTag,
  RequiredPromotionTag,
} from '@koob/ui';
import { Stack } from '@koob/margaret';
import { useTranslation } from 'react-i18next';
import { Text, SectionCard } from 'ui';
import { formatCurrency } from 'utils';
import { useEffect, useState } from 'react';
import { uniqBy } from 'lodash';
import { BenefitsKindEnum } from '@koob/enums';
import { getBedChoicesForPax, getRoomPax } from '@koob/utils';

const CrossedPrice = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.textLighter};
  text-decoration: line-through;
`;

const CardTitle = styled(Text).attrs({ type: 'bodyLarge' })`
  font-weight: bold;

  &::first-letter {
    text-transform: uppercase;
  }
`;

const Legend = styled(Text).attrs({
  color: 'textLighter',
  type: 'bodySmall',
})``;

const UppercaseText = styled(Text)`
  text-transform: uppercase;
`;

const IconWrapper = styled.div`
  color: ${({ theme }) => theme.textLight};
`;

/**
 * The optional supplements of one bed, as the supplement select's options.
 *
 * Read per room, never shared between rooms: a supplement sold as a percentage
 * carries the same name on every room type and a value priced off THAT room's
 * night, so an early check-in at 10 % is 40 € on a 400 € suite and 10 € on a
 * 100 € standard. `value` is the priced surcharge the bed already carries.
 */
const toOptionalPromoOptions = (optionalSupplements) =>
  (optionalSupplements ?? [])
    // KD-3496 — an early check-in / late check-out is routinely offered at no
    // charge: what it carries is a room-night to hold outside the stay, not a
    // surcharge. The positive-value gate read a 0 as "not applicable" and hid
    // the service entirely, so it now applies only to ordinary supplements.
    .filter(
      (supplement) =>
        Number(supplement?.value) > 0 || Boolean(supplement?.code),
    )
    .map((supplement) => ({
      id: supplement?.id,
      displayName: supplement?.name,
      value: supplement?.name,
      // `?? 0` guards the sums downstream: Math.round(undefined) is NaN, which
      // would poison every optional-promo total once it reaches one.
      price: Math.round(supplement?.value ?? 0),
      priceInDollar: Math.round(supplement?.priceInDollar ?? 0),
      kind: supplement?.kind,
      code: supplement?.code,
      // KD-3496 — the rate the slot is sold at, quoted in the extra-nights
      // note; its hours are derived from the code.
      benefitValue: supplement?.benefitValue,
    }));

const DmcContractHotelRooms = ({
  rooms,
  numberOfNights,
  roomsForRecap,
  modifyCurrentRooms,
  setCurrentSelectedBedIndex,
  currentSelectedBedIndex,
  updateRoomFromPromotion,
  optionalPromo,
  setOptionalPromo,
  selectedRooms,
  setSelectedPromo,
  currentPossibility,
  handleProposalRoom,
  updateRoomSupplements,
}) => {
  const { t } = useTranslation(['bookings', 'hotelRoom']);
  const theme = useTheme();
  const [selectedPromotionForRoom, setSelectedPromotionForRoom] = useState(
    Array(rooms.length).fill(0),
  );
  const [roomsForRecapPrevState, setRoomsForRecapPrevState] = useState(
    JSON.stringify(roomsForRecap),
  );
  const [selectedOptionalPromos, setSelectedOptionalPromos] = useState([]);

  const supplementTypes = ['supplement', 'supplement_percent'];

  useEffect(() => {
    let tmp = [...selectedPromotionForRoom];
    const currentRoomsForRecapState = JSON.stringify(roomsForRecap);

    if (currentRoomsForRecapState !== roomsForRecapPrevState) {
      const previousRooms = JSON.parse(roomsForRecapPrevState);
      const currentRooms = JSON.parse(currentRoomsForRecapState);

      currentRooms.forEach((currRoom, index) => {
        const previousRoom = previousRooms[index];
        if (previousRoom?.name !== currRoom.name) {
          tmp[index] = 0;
        }
      });
      if (currentRooms.length < tmp) {
        tmp = tmp
          .slice(0, currentRooms.length)
          .concat(new Array(tmp.length - currentRooms.length).fill(0));
      }
      setRoomsForRecapPrevState(currentRoomsForRecapState);
      setSelectedPromotionForRoom(tmp);
      setOptionalPromo([]);
      if (!selectedRooms?.find((item) => !!item?.bed?.id)) {
        setSelectedOptionalPromos([]);
      }
    }
  }, [
    roomsForRecap,
    roomsForRecapPrevState,
    selectedPromotionForRoom,
    setOptionalPromo,
  ]);

  const handleUpdatedPromos = (index, value) => {
    setSelectedOptionalPromos((prev) => {
      const updatedPromos = {
        index,
        promos: value.map((promo) => ({ ...promo })),
      };
      const updatedArray = [...prev];
      updatedArray[index] = updatedPromos;
      return updatedArray;
    });
  };

  return (
    <Stack direction="column" size="full" gutterSize={0.5}>
      <Stack paddingBottom={0.5}>
        <Text type="h2">{t('mySelection')}</Text>
      </Stack>

      {roomsForRecap.map((selectedRoom, roomSelectedIndex) => {
        const promotionInfo = selectedRoom?.bed?.promotionInfo;
        const isSupplementPromo =
          promotionInfo?.kind && !supplementTypes.includes(promotionInfo?.kind);
        const promoId = isSupplementPromo
          ? promotionInfo?.promotionId
          : undefined;

        let promotionIndex = 0;
        // The supplements of the bed actually retained for THIS room. Reading
        // them off the room being chosen and broadcasting that list here priced
        // every card off that one room: the select then offered — and the recap
        // summed — a suite's early check-in on a standard room.
        const optionalPromoList = uniqBy(
          toOptionalPromoOptions(selectedRoom?.bed?.optionalSupplements).filter(
            ({ id }) => Boolean(id),
          ),
          'displayName',
        );

        const promoOptional = selectedOptionalPromos[roomSelectedIndex]?.promos;
        const OptionalPromoprices = (promoOptional || []).reduce(
          (total, { price }) => total + price,
          0,
        );
        const OptionalPromoPriceInDollar = (promoOptional || []).reduce(
          (total, { priceInDollar }) => total + priceInDollar,
          0,
        );
        const promotionOptions =
          selectedRoom?.allPromotions
            ?.flatMap((promotionCombinations) => {
              return Object.entries(promotionCombinations).map(
                ([_, promotionCombination]) => {
                  if (selectedRoom?.bed.id && promotionCombination.length > 0) {
                    const result = {
                      label: promotionCombination.join(', '),
                      value: promotionIndex,
                      index: promotionIndex,
                      promotions: promotionCombination,
                      promotionId: promoId,
                      bedName: selectedRoom?.bed.name,
                      originalIndex: selectedRoom.originalIndex,
                    };
                    promotionIndex++;
                    return result;
                  } else {
                    return null;
                  }
                },
              );
            })
            .filter((item) => item !== null) ?? [];
        const uniqueDataPromo = Object.values(
          promotionOptions?.reduce(
            (acc, obj) => ({ ...acc, [obj.label]: obj }),
            {},
          ),
        );

        const promotions = !supplementTypes.includes(
          selectedRoom?.bed?.promotionInfo?.kind,
        )
          ? selectedRoom?.bed?.promotions
          : [];
        const allMandatorySupplements = selectedRoom?.bed?.mandatorySupplements;
        const mandatorySupplements = Object.entries(
          (allMandatorySupplements || []).reduce((acc, val) => {
            acc[val] = (acc[val] || 0) + 1;
            return acc;
          }, {}),
        ).map(([key, value]) => `${key} (x${value})`);
        // KD-3732 — the availability lists every bed configuration that suits
        // ANY of the requested rooms: a room of 1 was also offered the extra
        // beds meant for a room of 3. Only those that suit THIS room's
        // travellers are offered, plus the one it already holds. `bedIndex`
        // stays the configuration's position in the full list, which is what
        // the proposal is built from.
        const fittingBedChoices = getBedChoicesForPax(
          selectedRoom,
          getRoomPax(rooms[roomSelectedIndex]),
        );
        const options = (selectedRoom.bedChoices ?? [])
          .map((bedChoice, bedIndex) => {
            return {
              value: bedChoice?.id,
              // Own inventory (KOOB) returns no nameWithPrice; fall back to the
              // bed name so the composition dropdown options aren't blank.
              label: bedChoice?.nameWithPrice ?? bedChoice?.name,
              bedIndex: bedIndex,
              roomIndex: roomSelectedIndex,
              fits:
                fittingBedChoices.includes(bedChoice) ||
                bedChoice?.id === selectedRoom?.bed?.id,
            };
          })
          .filter(({ fits }) => fits);
        // Match on the full bed id: the recap room's bed is always one of its
        // own bedChoices, so an exact id match reliably reflects the current
        // composition. Matching a single `##`-segment breaks for suppliers
        // whose id format has no distinct composition segment (the select then
        // stays stuck on the first option).
        const selectedOption = options.find(
          (elem) => elem.value === selectedRoom?.bed?.id,
        );

        const optionalSupplementValue =
          selectedRooms[roomSelectedIndex]?.optionalPromos ?? optionalPromo;

        return (
          <SectionCard
            key={roomSelectedIndex}
            isSelected={roomSelectedIndex === roomsForRecap.length - 1}
            onClick={() => {
              if (roomSelectedIndex !== roomsForRecap.length - 1) {
                modifyCurrentRooms(roomSelectedIndex);
                setSelectedOptionalPromos([]);
              }
            }}
          >
            <Stack direction="column" size="full">
              <Stack direction="column" size="full" gutterSize={0.125}>
                <Stack
                  size="full"
                  gutterSize={0.25}
                  alignX="space-between"
                  alignY="baseline"
                >
                  <div>
                    <UppercaseText color="textLighter">
                      {t('roomN', { count: roomSelectedIndex + 1 })}
                    </UppercaseText>
                  </div>

                  <IconWrapper>
                    <i className="fa-regular fa-pen" style={{ fontSize: 20 }} />
                  </IconWrapper>
                </Stack>

                <Stack
                  size="full"
                  gutterSize={0.125}
                  alignX="space-between"
                  alignY="baseline"
                  direction="column"
                >
                  <Stack direction="column" gutterSize={0.375}>
                    <CardTitle>{selectedRoom?.name}</CardTitle>

                    <Legend>
                      <Stack gutterSize={0.25} alignY="center">
                        <i
                          className="fa-light fa-users"
                          style={{ fontSize: 18 }}
                        />

                        <span>
                          {t('nGuests', {
                            count:
                              rooms[roomSelectedIndex].adults +
                              rooms[roomSelectedIndex].children,
                          })}
                        </span>
                      </Stack>
                    </Legend>
                  </Stack>

                  {options?.length > 1 && (
                    <Stack
                      direction="column"
                      gutterSize={0.5}
                      style={{ paddingBlock: theme.spacing(0.5) }}
                    >
                      <Text type="bodySmall">
                        {t('composition', { ns: 'hotelRoom' })}
                      </Text>

                      <KoobInputSelect
                        value={selectedOption?.value ?? null}
                        options={options}
                        disabled={selectedRoom?.isLast}
                        onValueChange={(value) => {
                          const option = options.find(
                            (item) => item.value === value,
                          );
                          if (!option) {
                            return;
                          }
                          setCurrentSelectedBedIndex(option.bedIndex);
                          handleProposalRoom(
                            option.bedIndex,
                            currentPossibility,
                          );
                        }}
                        data-testid={`composition_${roomSelectedIndex}`}
                      />
                    </Stack>
                  )}

                  <Stack
                    direction="column"
                    gutterSize={0.5}
                    style={{ paddingBlock: theme.spacing(0.5) }}
                  >
                    {uniqueDataPromo?.length > 1 && (
                      <>
                        <Text type="bodySmall">{t('choosePromotion')}</Text>

                        <KoobInputSelect
                          value={
                            (
                              uniqueDataPromo[
                                selectedPromotionForRoom[roomSelectedIndex]
                              ] ?? uniqueDataPromo[0]
                            )?.value ?? null
                          }
                          data-testid={`select-promotion-${roomSelectedIndex}`}
                          options={uniqueDataPromo}
                          onValueChange={(selectedValue) => {
                            const value = uniqueDataPromo.find(
                              (item) => item.value === selectedValue,
                            );
                            if (!value) {
                              return;
                            }
                            const tmp = [...selectedPromotionForRoom];
                            tmp[roomSelectedIndex] = Math.max(
                              0,
                              uniqueDataPromo.findIndex((p) => p === value),
                            );
                            setSelectedPromotionForRoom(tmp);
                            setCurrentSelectedBedIndex(currentSelectedBedIndex);
                            updateRoomFromPromotion(
                              value.originalIndex,
                              value.label,
                              value.bedName,
                              selectedRoom,
                            );
                            setSelectedPromo(value);
                            handleProposalRoom(
                              currentSelectedBedIndex,
                              currentPossibility,
                            );
                          }}
                        />
                      </>
                    )}

                    {(optionalPromoList || []).length > 0 && (
                      <KoobInputSelect
                        placeholder={t('supplement')}
                        options={optionalPromoList.map((promo) => ({
                          value: promo?.id,
                          label: promo?.displayName,
                        }))}
                        data-testid={`select-supplement-${roomSelectedIndex}`}
                        value={(
                          selectedRooms[roomSelectedIndex]?.optionalPromos ??
                          optionalPromo ??
                          []
                        ).map((promo) => promo?.id)}
                        onValueChange={(ids) => {
                          const value = optionalPromoList.filter((promo) =>
                            (ids ?? []).includes(promo?.id),
                          );
                          handleUpdatedPromos(roomSelectedIndex, value);
                          // A room already in `selectedRooms` reads its own
                          // `optionalPromos` back into this select, so the
                          // change has to land there or it is invisible and
                          // never reaches the booking payload. `optionalPromo`
                          // is the PENDING room's selection and must not be
                          // touched from a validated row: it would re-price the
                          // room still being chosen and warn about an extra
                          // night on it.
                          if (selectedRooms?.[roomSelectedIndex]) {
                            updateRoomSupplements?.(roomSelectedIndex, value);
                          } else {
                            setOptionalPromo(value);
                          }
                          handleProposalRoom(
                            roomSelectedIndex,
                            currentPossibility,
                          );
                        }}
                        multiple
                      />
                    )}
                  </Stack>

                  <Stack
                    style={{
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                      marginBlock: theme.spacing(0.5),
                    }}
                  >
                    {promotions?.length > 0 &&
                      promotions.map(
                        (promotion, index) =>
                          promotion !== null && (
                            <>
                              {promotionInfo?.kind ===
                              BenefitsKindEnum.UPGRADE_ROOM ? (
                                <KoobTooltip
                                  tip={`Upgraded room from ${promotionInfo?.upgradedFromRoom} to ${promotionInfo?.upgradedToRoom}`}
                                  position="left"
                                  hasArrow={true}
                                  color="purple"
                                >
                                  <PromotionTag
                                    name={promotion}
                                    kind={promotion}
                                    key={index}
                                  />
                                </KoobTooltip>
                              ) : (
                                <PromotionTag
                                  name={promotion}
                                  kind={promotion}
                                  key={index}
                                />
                              )}
                            </>
                          ),
                      )}

                    {mandatorySupplements?.length > 0 &&
                      mandatorySupplements.map((supplement, index) => (
                        <RequiredPromotionTag
                          key={index}
                          name={supplement}
                          label={t('requiredSupplement')}
                        />
                      ))}
                  </Stack>

                  <Stack
                    style={{
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                      marginBlock: theme.spacing(0.5),
                    }}
                  >
                    {(optionalSupplementValue || []).map(
                      ({ displayName, kind }, index) => (
                        <PromotionTag
                          name={displayName}
                          key={index}
                          kind={kind}
                        />
                      ),
                    )}
                  </Stack>

                  <Stack
                    alignX="flex-end"
                    style={{ flexWrap: 'wrap', gap: '0.5rem' }}
                  >
                    <Stack
                      direction="column"
                      alignX="flex-end"
                      className="border p-2 rounded-xl"
                    >
                      <Stack
                        alignY="center"
                        alignX="flex-end"
                        style={{ flexWrap: 'wrap', gap: '0 0.5rem' }}
                      >
                        {selectedRoom?.bed?.totalPriceWithPromo !==
                          selectedRoom?.bed?.totalPriceWithoutPromo && (
                          <CrossedPrice style={{ whiteSpace: 'nowrap' }}>
                            {formatCurrency({
                              amount: selectedRoom?.bed?.totalPriceWithoutPromo,
                              currency: selectedRoom?.bed?.currency,
                            })}
                          </CrossedPrice>
                        )}

                        <Text
                          type="bodyLarge"
                          color="text"
                          fontWeight={700}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {formatCurrency({
                            amount:
                              selectedRoom?.bed?.totalPriceWithPromo +
                              OptionalPromoprices,
                            currency: selectedRoom?.bed?.currency,
                          })}
                        </Text>
                      </Stack>

                      <Legend>
                        {t('pricePerNight', {
                          price: formatCurrency({
                            amount:
                              (selectedRoom?.bed?.totalPriceWithPromo +
                                OptionalPromoprices) /
                              numberOfNights,
                            currency: selectedRoom?.bed?.currency,
                          }),
                        })}
                      </Legend>
                    </Stack>

                    {selectedRoom?.bed?.totalPriceInDollar?.withPromo ? (
                      <Stack
                        direction="column"
                        alignX="flex-end"
                        className="border p-2 rounded-xl"
                      >
                        <Stack
                          alignY="center"
                          alignX="flex-end"
                          style={{ flexWrap: 'wrap', gap: '0 0.5rem' }}
                        >
                          {selectedRoom?.bed?.totalPriceWithPromo !==
                            selectedRoom?.bed?.totalPriceWithoutPromo && (
                            <CrossedPrice style={{ whiteSpace: 'nowrap' }}>
                              {formatCurrency({
                                amount:
                                  selectedRoom?.bed?.totalPriceInDollar
                                    ?.withoutPromo,
                                currency: 'usd',
                              })}
                            </CrossedPrice>
                          )}

                          <Text
                            type="bodyLarge"
                            color="text"
                            fontWeight={700}
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            {formatCurrency({
                              amount:
                                parseFloat(
                                  selectedRoom?.bed?.totalPriceInDollar
                                    ?.withPromo ?? '0',
                                ) + OptionalPromoPriceInDollar,
                              currency: 'usd',
                            })}
                          </Text>
                        </Stack>

                        <Legend>
                          {t('pricePerNight', {
                            price: formatCurrency({
                              amount:
                                (parseFloat(
                                  selectedRoom?.bed?.totalPriceInDollar
                                    ?.withPromo + OptionalPromoPriceInDollar ??
                                    '0',
                                ) +
                                  OptionalPromoPriceInDollar) /
                                numberOfNights,
                              currency: 'usd',
                            }),
                          })}
                        </Legend>
                      </Stack>
                    ) : null}

                    {selectedRoom?.instantAvailability && (
                      <KoobTooltip
                        tip={
                          selectedRoom?.supplier?.toUpperCase() === 'KOEDIA'
                            ? t('connectedChannelManager:tooltip')
                            : t('allotments:tooltip')
                        }
                        hasArrow={false}
                        color="purple"
                      >
                        <div className="bg-green-100 rounded-full h-6 w-6 flex items-center justify-center">
                          <i className="fa fa-bolt text-green-500" />
                        </div>
                      </KoobTooltip>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </SectionCard>
        );
      })}
    </Stack>
  );
};

export default DmcContractHotelRooms;
